<template>
  <div>
    <v-container fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">

          <v-alert
              v-if="errors.length"
              border="top"
              dismissible
              prominent
              type="error"
          >
            <ul class="fs-15">
              <li v-for="(error, index) in errors" :key="index">
                {{ error.message }}
              </li>
            </ul>
          </v-alert>


          <Overlay :overlay="isLoading"/>

          <InfoPlanning v-if="step === 0"
                        :form.sync="form"
                        :step.sync="step"
                        @next="step = 1"/>

          <Animators v-if="step === 1"
                     :form.sync="form"
                     :step.sync="step"
                     @back="step = 0"
                     @next="step = 2"/>

          <Scenarios v-if="step === 2"
                     :gifts="gifts"
                     :form.sync="form"
                     :step.sync="step"
                     @back="step = 1"
                     @save="save"/>


          <ConfirmDialog ref="confirmDialog"/>

        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import InfoPlanning from "@/views/v2_campaigns/create/components/InfoPlanning.vue";
import Animators from "@/views/v2_campaigns/create/components/Animators.vue";
import Scenarios from "@/views/v2_campaigns/create/components/Scenarios";
import {HTTP} from "@/http-common";

const MEMORY_IMAGES_COUNT = 6

const emptyMemoryImage = () => ({file: null, preview: null, data: null, existing: null})
const emptyMemoryImages = () => Array.from({length: MEMORY_IMAGES_COUNT}, emptyMemoryImage)

export default {
  components: {Scenarios, Animators, InfoPlanning},
  data() {
    return {
      isLoading: false,
      errors: [],
      gifts: [],
      step: 0,
      form: {
        id: null,
        name: null,
        start_date: null,
        end_date: null,
        description: null,
        color: '#23b7e5',
        image: null,
        type: 'spintowin',
        memory_images: emptyMemoryImages(),
        memory_background_image: {file: null, preview: null, data: null, existing: null},
        animators: [],
        scenarios: [{
          name: null,
          min_product_qty: 1,
          brands: [],
          products: [],
          purchased_products: [],
          purchased_brands: [],
          gifts: [],
        }],
      }
    }
  },
  computed: {
    isUpdate() {
      return !!this.$route.params.id && this.$route.name === 'v2-campaigns.update'
    }
  },
  async mounted() {
    await this.getGifts()
    if (this.isUpdate) await this.showCampaign()
  },
  methods: {
    /** Normalize API errors (Laravel 422) into [{message}] */
    normalizeErrors(data) {
      const raw = data?.errors ?? data ?? {}
      if (typeof raw === 'string') return [{message: raw}]
      return Object.values(raw).flat().map(message => ({message}))
    },

    buildPayload() {
      const isMemory = this.form.type === 'memory_game'
      return {
        ...this.form,
        memory_background_image: this.form.memory_background_image?.data || null,
        memory_images: isMemory
            ? this.form.memory_images.map(img => ({
              image: img.data,          // base64 if new file selected
              existing: img.existing,   // path if unchanged
            }))
            : [],
      }
    },

    validate() {
      const errors = []
      if (this.form.type === 'memory_game') {
        const missing = this.form.memory_images.some(img => !img.data && !img.existing)
        if (missing) errors.push({message: `Veuillez ajouter les ${MEMORY_IMAGES_COUNT} images du jeu.`})
      }
      this.errors = errors
      return !errors.length
    },

    async save() {
      if (!this.validate()) {
        this.$vuetify.goTo(0)
        return
      }

      const confirmed = await this.$refs.confirmDialog.open('', 'Êtes-vous sûr de vouloir enregistrer cette campagne ?', {
        color: 'orange',
        agreeText: 'Enregistrer',
        icon: 'mdi-information-outline',
      })
      if (!confirmed) return

      this.isLoading = true
      this.errors = []
      const url = this.form.id ? "/v2-campaigns/update" : "/v2-campaigns/store"

      try {
        await HTTP.post(url, this.buildPayload())
        this.$successMessage = "Cette campagne a été enregistrée avec succès"
        this.$router.push('/v2-campaigns')
      } catch (err) {
        console.log(err)
        this.$vuetify.goTo(0)
        if (err.response && err.response.status === 422) {
          this.errors = this.normalizeErrors(err.response.data)
        } else {
          this.$errorMessage = "Une erreur s'est produite, veuillez réessayer."
        }
      } finally {
        this.isLoading = false
      }
    },

    mapMemoryImages(images = []) {
      // supports ["path", ...] or [{path|image: "..."}, ...]
      const slots = emptyMemoryImages()
      images.slice(0, MEMORY_IMAGES_COUNT).forEach((item, i) => {
        slots[i].existing = typeof item === 'string' ? item : (item.path ?? item.image ?? null)
      })
      return slots
    },

    mapScenario(el) {
      return {
        id: el.id,
        name: el.name,
        min_product_qty: el.min_product_qty,
        brands: el.original_brands.map(i => i.id),
        products: el.original_products.map(i => i.id),
        purchased_products: el.purchased_products.map(i => i.id),
        purchased_brands: el.purchased_brands.map(i => i.id),
        gifts: this.gifts.map(gift => {
          const giftData = el.gifts.find(g => g.id === gift.id) || null
          return {
            id: gift.id,
            image: gift.image,
            name: gift.name,
            is_digital: gift.is_digital,
            type: gift.type,
            percentage: giftData ? giftData.pivot.percentage : null,
            digital_amount: giftData ? giftData.pivot.digital_amount : null,
            recipient_digital_amount: giftData ? giftData.pivot.recipient_digital_amount : null,
            is_active: !!giftData,
          }
        }),
      }
    },

    async showCampaign() {
      this.isLoading = true
      try {
        const res = await HTTP.get(`/v2-campaigns/${this.$route.params.id}/show`)
        const campaign = res?.data?.data
        if (!campaign) return this.$router.push('/404')

        this.form = {
          id: campaign.id,
          name: campaign.name,
          start_date: campaign.start_date,
          end_date: campaign.end_date,
          description: campaign.description,
          color: campaign.color,
          image: campaign.image,
          memory_background_image: {
            file: null,
            preview: null,
            data: null,
            existing: campaign.memory_background_image || null,
          },
          type: campaign.type,
          memory_images: this.mapMemoryImages(campaign.memory_images),
          scenarios: campaign.scenarios.map(this.mapScenario),
          animators: campaign.animators.map(el => ({...el, is_active: true})),
        }
      } catch (err) {
        console.log(err)
      } finally {
        this.isLoading = false
      }
    },

    async getGifts() {
      this.isLoading = true
      try {
        const res = await HTTP.get('/gifts')
        this.gifts = res.data.data

        const freshGifts = () => this.gifts.map(g => ({
          ...g,
          percentage: null,
          digital_amount: null,
          recipient_digital_amount: null,
          is_active: false,
        }))
        this.form.scenarios.forEach(s => {
          s.gifts = freshGifts()
        })
      } catch (err) {
        console.log(err)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>

</style>